import { store } from "src/store";
import moment from "moment";

// Logger
import { logDebug, logError, logMeasurement, logTrace, logWarn } from "src/services/logger";

// Actions
import { wsConnected, wsDisconnected, updateAvailablePas, AppStatus, loadPasStatus, updateLastStatusDate } from "src/store/context/reducer";


// Configs
export let ws: WsInstance;

type WsConfig = {
  queuesArray: Array<string>;
  paName: string;
  paRamal: number;
  paId: string;
  hasMorgaCertificate: boolean;
};

type WsInstance = {
  sendMessage: Function;
  request: Function;
  registerMetricListener: Function;
  disconnect: Function;
  connect: Function;
  on: Function;
  clearBinds: Function;
};

type WsRequestOptions = {
  responseType?: string;
  timeoutMs?: number;
};

const DEFAULT_REQUEST_TIMEOUT_MS = 15000;

interface CustomWebSocket extends WebSocket {
  id?: number;
  ping?: Function;
  lastCheck?: number;
  pingSentAt?: number;
}

interface WsInstanceOptions {
  onMessage?: Function;
  onError?: Function;
  onOpen?: Function;
  onClose?: Function;
  isReconnection?: boolean;
}

const getWsInstance = (config: WsConfig, options: WsInstanceOptions): Promise<CustomWebSocket> => {
  return new Promise((resolve, reject) => {
    const wsAddress = import.meta.env.VITE_WS_URL + `?uuid=${config.paId}`;

    // Create instance
    try {
      const websocket: CustomWebSocket = new WebSocket(wsAddress);

      websocket.id = Date.now();
      websocket.ping = () => {
        websocket.pingSentAt = new Date().getTime();
        websocket?.send(JSON.stringify({ type: "PING" }));
      };

      // Bind messages
      websocket.onmessage = async (message) => {
        return options.onMessage && options.onMessage(message);
      };

      // Bind openning
      websocket.onopen = () => {
        options.onOpen && options.onOpen(websocket, options.isReconnection);
        return resolve(websocket);
      };

      // Bind closing
      websocket.onclose = (e) => {
        return options.onClose && options.onClose(e);
      };

      // Bind error
      websocket.onerror = (e) => {
        reject(e);
        return options.onError && options.onError(e);
      };
    } catch (ex) {
      reject(ex);
      return options.onError && options.onError(ex);
    }
  });
};

export async function createWsClient(config: WsConfig): Promise<WsInstance> {
  let websocket: CustomWebSocket;
  let readyCheckTimer: any;
  let reconnectAttempts = 0;

  const logContext = () => ({ paId: config.paId, ramal: config.paRamal });

  // Some binds
  const binds: Array<{ ref: number; eventName: string; callback: Function }> = [];

  // Pending request/response correlation (key = response event type)
  const pending: Array<{ type: string; resolve: Function; reject: Function; timer: any }> = [];

  // Metric listeners (RTT / reconnects) consumed by the network monitor
  const metricListeners: Array<(metric: { rtt: number | null; reconnectCount: number }) => void> = [];

  const publishMetric = (metric: { rtt: number | null; reconnectCount: number }) => {
    metricListeners.forEach((listener) => listener(metric));
  };

  const registerMetricListener = (cb: (metric: { rtt: number | null; reconnectCount: number }) => void) => {
    metricListeners.push(cb);

    return () => {
      const index = metricListeners.indexOf(cb);
      if (index !== -1) metricListeners.splice(index, 1);
    };
  };

  // => HANDLERS
  const onMessage = async (message: any) => {
    const event = JSON.parse(message.data);

    // Resolve pending requests by response event type
    const pendingIndex = pending.findIndex((p) => p.type === event.type);
    if (pendingIndex !== -1) {
      const [entry] = pending.splice(pendingIndex, 1);
      clearTimeout(entry.timer);
      entry.resolve(event.data);
    }

    const bindHandlers = binds.filter((b) => b.eventName === event.type);

    // Call binds
    if (bindHandlers) await Promise.all(bindHandlers.map((b) => b.callback.call(null, event.data)));

    // Log (trace-level por volume)
    if (event.type != "PA_STATUS_UPDATE") logTrace(`Websockets: ${event.type}`, { ...logContext(), eventType: event.type });

    switch (event.type) {
      case "PA_STATUS_UPDATE": {
        const fullState = store.getState();

        if (fullState.auth.userInfo.id === event.data.paQueueId) {
          const date = new Date(event.data.statusUpdatedAt).getTime();
          store.dispatch(updateLastStatusDate(date));
        } else {
          store.dispatch(updateAvailablePas(event.data));
        }

        break;
      }

      case "LIST_PAS_STATUS":
        store.dispatch(loadPasStatus(event.data));
        break;

      case "PONG": {
        const now = new Date().getTime();
        const rtt = websocket.pingSentAt ? now - websocket.pingSentAt : null;

        websocket.lastCheck = now;
        websocket.pingSentAt = undefined;

        publishMetric({ rtt, reconnectCount: reconnectAttempts });
        break;
      }

      default:
        return false;
    }
  };

  const onOpen = (ws: CustomWebSocket, isReconnection: boolean) => {
    logDebug("Websockets: conexão aberta", { ...logContext(), isReconnection });

    // Update WS
    websocket = ws;

    const {
      config: stateConfig,
      context,
      auth: { userInfo, paused },
      call: { callDetails },
    } = store.getState();

    let event: any;

    const joinQueueEvent = {
      type: "PA_REGISTERED_ON_QUEUES",
      data: {
        queues: stateConfig.queues || ["oculta"],
        paName: config.paName,
        ramal: stateConfig.sip.paNumber || config.paRamal,
        status: "AVAILABLE",
      },
    };

    const needsRestore = context.appStatus === AppStatus.IN_CALL || context.appStatus === AppStatus.IN_PAUSE;

    // Rejoin queue
    if (isReconnection) {
      sendMessage(joinQueueEvent);
    }

    // Restore
    if (needsRestore) {
      event = {
        type: "PA_RESTORE_STATE",
        data: {
          id: userInfo.id,
          paName: userInfo.name,
          queues: stateConfig.queues,
          ramal: stateConfig.sip.paNumber,
          /*   ramalVoip: accessDotNotation(
            self,
            "sipConfigurations.defaultSipConfiguration.ramal"
          )
            ? accessDotNotation(
                self,
                "sipConfigurations.defaultSipConfiguration.ramal"
              )
            : self.pa().ramal, */
        },
      };

      if (context.appStatus === AppStatus.IN_PAUSE) {
        event.data.status = "PAUSED";
        event.data.pauseType = paused?.texto;
      }

      const nome = callDetails?.uda.usuario.nome;
      if (nome) event.data.restoredUdaName = nome;
    } else if (!isReconnection) {
      event = {
        type: "PA_REGISTERED_ON_QUEUES",
        data: {
          queues: stateConfig.queues || ["oculta"],
          paName: config.paName,
          ramal: stateConfig.sip.paNumber || config.paRamal,
          status: "AVAILABLE",
          hasMorgaCertificate: !!config.hasMorgaCertificate,
        },
      };
    }

    sendMessage(event);

    // Update status
    store.dispatch(wsConnected());

    // Check
    websocket.lastCheck = new Date().getTime();
  };

  const onClose = async (e: any) => {
    store.dispatch(wsDisconnected());

    // Check reason
    if (e.code !== 1000) {
      reconnectAttempts += 1;

      logWarn("Websockets: fechamento inesperado", { ...logContext(), code: e.code, reason: e.reason ?? null });
      logMeasurement("ws_reconnect_attempts", { count: reconnectAttempts }, logContext());

      publishMetric({ rtt: null, reconnectCount: reconnectAttempts });

      // Reconnect
      await connect(true);
    }
  };

  const onError = (e: any) => {
    logError("Websockets: erro", e, logContext());

    store.dispatch(wsDisconnected());
  };

  // METHODS
  const sendMessage = (message: object) => {
    const messageType = (message as any)?.type ?? null;

    logTrace("Websockets: mensagem enviada", { ...logContext(), eventType: messageType });

    try {
      const _message = JSON.stringify(message);
      websocket.send(_message);

      return { success: true };
    } catch (error) {
      logError("Websockets: falha ao enviar mensagem", error, { ...logContext(), eventType: messageType });

      return {
        success: false,
      };
    }
  };

  const request = (message: object, options: WsRequestOptions = {}): Promise<any> => {
    const messageType = (message as any)?.type ?? "";
    const responseType = options.responseType || `${messageType}_INFO`;
    const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

    const result = sendMessage(message);

    if (!result.success) {
      return Promise.reject(new Error("Falha ao enviar mensagem via websocket"));
    }

    return new Promise((resolve, reject) => {
      // Single-flight por tipo: rejeita pendência anterior do mesmo tipo
      const prevIndex = pending.findIndex((p) => p.type === responseType);
      if (prevIndex !== -1) {
        const [prev] = pending.splice(prevIndex, 1);
        clearTimeout(prev.timer);
        prev.reject(new Error(`Requisição ${responseType} substituída por uma nova`));
      }

      const timer = setTimeout(() => {
        const index = pending.findIndex((p) => p.type === responseType && p.resolve === resolve);
        if (index !== -1) pending.splice(index, 1);

        logWarn("Websockets: timeout de resposta", { ...logContext(), eventType: messageType, responseType, timeoutMs });
        reject(new Error(`Timeout ao aguardar resposta ${responseType}`));
      }, timeoutMs);

      pending.push({ type: responseType, resolve, reject, timer });
    });
  };

  const disconnect = () => {
    logDebug("Websockets: desconexão normal", logContext());

    // Clear interval
    clearInterval(readyCheckTimer);

    // Send close 1000
    websocket.close(1000);
  };

  const clearBinds = (bindsArray: Array<number>) => {
    if (bindsArray)
      return bindsArray.forEach((id) => {
        const index = binds.findIndex((b) => b.ref === id);
        return !!binds.splice(index, 1).length;
      });

    return binds.splice(0, binds.length);
  };

  const on = (eventName: string, callback: Function) => {
    const ref = new Date().getTime();

    // Create remove ref

    // Push list
    binds.push({ ref, eventName, callback });

    return ref;
  };

  const wsOptions = {
    onMessage,
    onOpen,
    onClose,
    onError,
  };

  const connect = async (isReconnection = false) => {
    websocket = await getWsInstance(config, { ...wsOptions, isReconnection });

    // Define TICKTIMER
    if (readyCheckTimer) clearInterval(readyCheckTimer);

    readyCheckTimer = setInterval(function ping() {
      const now = new Date().getTime();

      if (websocket.lastCheck && now - websocket.lastCheck > 8000) {
        logWarn("Websockets: timeout de pong", {
          ...logContext(),
          lastPongAt: moment(websocket.lastCheck).format("HH:mm:ss"),
          deltaSeconds: Math.round((now - websocket.lastCheck) / 1000),
        });

        store.dispatch(wsDisconnected());

        websocket.close(3000);

        clearInterval(readyCheckTimer);

        return;
      }

      if (websocket.readyState === 1) websocket.ping && websocket.ping();
    }, 2000);

    return websocket;
  };

  // UPDATE SINGLETON
  await connect();

  ws = { clearBinds, registerMetricListener, request, sendMessage, disconnect, connect, on };

  return ws;
}
